
  import { Component, Watch } from 'vue-property-decorator'
  import { mapActions, mapGetters } from 'vuex'
  import { Filter } from '@/entities/public/Resource/interfaces'
  import { toRouteRecord } from '@/router/helpers'
  import { Resource } from '@/entities/public'
  import BaseBar from '@/layouts/BaseBar'
  import { Period } from '@/store/resources/period'
  import debounce from 'lodash/debounce'

@Component({
  methods: {
    ...mapActions('resources/form', ['fetchData']),
    ...mapActions('resources', ['setActive', 'setFilter', 'setPeriod']),
  },
  computed: {
    ...mapGetters('resources', ['all', 'active']),
    ...mapGetters('resources/datatables', ['datatableLoading']),
  },
})
  export default class AppBar extends BaseBar {
  private setActive!: (resource?: Resource) => void;
  private setFilter!: (filter?: Filter) => void;
  private setPeriod!: (period?: Period) => void;
  private active!: Resource;
  private all!: Array<Resource>;
  private navigationPromise: Promise<void> | null = null;

  datatableLoading!: boolean;
  resource = -1;
  option = -1;

  // Método debounced para la navegación
  private debouncedNavigate = debounce(async (path: string, query: Record<string, any>) => {
    try {
      await this.$router.push({ path, query })
    } catch (error: any) {
      // Solo logueamos errores que no sean de navegación cancelada
      if (error?.name !== 'NavigationCancelled' && error?.name !== 'NavigationDuplicated') {
        console.error('Error durante la navegación:', error)
      }
    }
  }, 300)

  created () {
    // Inicializar el recurso activo basado en la ruta actual
    const { $route: { path }, targets } = this

    if (path && targets && targets.length) {
      const initialIndex = targets.findIndex(target => target.path === path)
      this.resource = initialIndex >= 0 ? initialIndex : 0

      // Asegurarse de que el recurso activo esté sincronizado
      if (initialIndex >= 0) {
        const activeResource = targets[initialIndex]
        this.setActive(activeResource)
      }
    } else {
      // Establecer valores por defecto si no hay coincidencia
      this.resource = 0
    }
  }

  get targets (): Array<Resource> {
    const { $route, all } = this
    const { name } = toRouteRecord($route)
    return all.filter(({ route }) => route?.name === name)
  }

  @Watch('isResize', { immediate: true })
  onResizeChange (val) {
    this.resize = val
  }

  @Watch('$route', { immediate: true, deep: true })
  onRouteChange (val, prev) {
    if (val?.name !== prev?.name) {
      this.updateSelectedTab()
    }
  }

  @Watch('resource', { immediate: true })
  async onResourceChange (val: number) {
    // Si el valor es inválido o no hay targets, retornamos
    if (val === -1 || !this.targets || !this.targets.length) return

    const target = this.targets[val]

    // Validación del target
    if (!target || !target.path) {
      console.warn('Target inválido o sin ruta definida:', target)
      return
    }

    // Actualizamos el estado antes de la navegación
    this.setActive(target)

    // Preservamos los query params actuales
    const query = { ...this.$route.query }

    // Utilizamos el método debounced para la navegación
    this.debouncedNavigate(target.path, query)
  }

  // --------------------------------
  get target (): Resource | undefined {
    const { resource, targets } = this
    if (!targets) return undefined

    return targets[resource]
  }

  get filters (): Array<Filter> | undefined {
    const { target } = this

    if (!target) return undefined

    return target.filters
  }

  get activeFilter () {
    const { option, filters } = this
    if (!filters) return undefined

    return filters[option]
  }

  @Watch('$route.query.search', { immediate: true })
  onSearchChange (val) {
    if (val) {
      this.option = this.filters?.findIndex(({ name }) => name === 'all') || 0
    }
  }

  @Watch('target')
  async onTargetChange (resource: Resource, prev: Resource) {
    if (!resource || prev === resource) return
    this.setActive(resource)
  }

  @Watch('activeFilter', { immediate: true, deep: true })
  async onFilterChange (filter: Filter, prev: Filter) {
    // Si no hay filtro, no hacemos nada
    if (!filter) return
    await this.setFilter(filter)

    try {
      // Preparamos el query con el filtro actual
      const query = {
        ...this.$route.query,
        filter: filter.name || undefined, // Si no hay nombre de filtro, lo removemos del query
      }

      // Removemos el filtro del query si es el filtro por defecto
      if (filter.name === 'all') {
        delete query.filter
      }

      // Actualizamos la ruta solo si el filtro ha cambiado
      if (filter.name !== this.$route.query.filter) {
        await this.$router.push({
          path: this.$route.path,
          query,
        }).catch(() => {
        })
      }
    } catch (error: any) {
      console.error('Error al aplicar el filtro:', error)
      // Solo ignoramos errores específicos de navegación
      if (error?.name !== 'NavigationCancelled' && error?.name !== 'NavigationDuplicated') {
        // Revertimos el filtro en caso de error
        this.setFilter(prev)
      }
    }
  }

  @Watch('active', { immediate: true, deep: true })
  onActiveChange (val) {
    if (!val) return

    const findPos = this.targets?.findIndex(({ id }) => id === val.id)
    const query = this.$route.query?.search
    if (findPos !== -1 && this.resource !== findPos && query) {
      this.resource = findPos
      this.option = this.filters?.[this.filters.length - 1] ? this.filters.length - 1 : 0
    } else if (findPos !== -1 && this.resource !== findPos) {
      this.resource = findPos
      this.option = 0
    }
  }

  // Método para manejar el cambio de tab de manera segura
  async handleTabChange (newValue: number) {
    // Validación básica del valor
    if (typeof newValue !== 'number' || newValue < 0) {
      console.warn('Valor de tab inválido:', newValue)
      return
    }

    // Cancelamos cualquier navegación pendiente
    this.debouncedNavigate.cancel()

    // Actualizamos directamente el recurso
    this.resource = newValue
  }

  // Limpiamos el debounce al destruir el componente
  beforeDestroy () {
    this.debouncedNavigate.cancel()
  }

  updateSelectedTab () {
    const { $route: { path }, targets } = this

    this.resource = (path && targets) ? targets.map(({ path }) => path).indexOf(path) : 0
  }
  }
